import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * The drawer refreshes from the exact section HTML returned by the add-to-cart
 * request before opening. This keeps the theme editor and storefront in sync
 * without a second cart request in the normal path.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = async (event) => {
    if (event.detail?.data?.didError) return;

    const cartItemsComponent = document.querySelector('cart-items-component');
    const sectionId = cartItemsComponent?.dataset.sectionId;
    const returnedSections = event.detail?.data?.sections || {};

    try {
      if (sectionId) {
        const sectionHTML = returnedSections[sectionId];

        if (sectionHTML) {
          await morphSection(sectionId, sectionHTML);
        } else {
          await sectionRenderer.renderSection(sectionId, { cache: false });
        }
      }
    } catch (error) {
      console.error('Cart drawer refresh failed:', error);
    }

    // Section morphing can replace the drawer element itself, so always query
    // the current element before opening it.
    const currentDrawer = document.querySelector('cart-drawer-component');
    currentDrawer?.showDialog?.();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
